import { apiV1Path } from '@/services/api-paths';

export type StaffOnlinePresence = 'online' | 'offline' | 'unknown';

export type StaffOnlineStatusRow = {
  status: string;
  online: boolean;
  presence: StaffOnlinePresence;
};

/** Cache curto em memória; sessionStorage cobre refresh na mesma aba. */
const CACHE_TTL_MS = 5 * 60_000;
const SESSION_CACHE_KEY = 'xat-staff-online-v1';

type CacheEntry = { at: number; row: StaffOnlineStatusRow };

const cache = new Map<string, CacheEntry>();

function hydrateCacheFromSession(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, CacheEntry>;
    const now = Date.now();
    for (const [id, entry] of Object.entries(parsed)) {
      if (!entry?.row || typeof entry.at !== 'number') continue;
      if (now - entry.at > CACHE_TTL_MS) continue;
      cache.set(id, entry);
    }
  } catch {
    /* ignore */
  }
}

function persistCacheToSession(): void {
  if (typeof window === 'undefined') return;
  try {
    const now = Date.now();
    const payload: Record<string, CacheEntry> = {};
    for (const [id, entry] of cache) {
      if (now - entry.at <= CACHE_TTL_MS) payload[id] = entry;
    }
    window.sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

hydrateCacheFromSession();

function normalizePresence(status: string, online: boolean): StaffOnlinePresence {
  if (online || status === 'online') return 'online';
  if (status === 'available') return 'unknown';
  if (
    status === 'not found' ||
    status === 'not_found' ||
    status === 'invalid' ||
    status === 'unavailable' ||
    status === 'offline'
  ) {
    return 'offline';
  }
  return 'offline';
}

function toRow(status: string, online: boolean): StaffOnlineStatusRow {
  const normalized = status.trim().toLowerCase() || 'unknown';
  return {
    status: normalized,
    online: online || normalized === 'online',
    presence: normalizePresence(normalized, online),
  };
}

function remember(id: string, row: StaffOnlineStatusRow): void {
  cache.set(id, { at: Date.now(), row });
}

function onlinecheckSingleUrl(id: string): string {
  // Em DEV: path relativo → middleware Vite (paralelo, sem API remota lenta).
  if (import.meta.env.DEV) {
    return `/api/v1/xat/onlinecheck/${encodeURIComponent(id)}`;
  }
  return apiV1Path(`/xat/onlinecheck/${encodeURIComponent(id)}`);
}

/** Lê entradas ainda válidas no cache (memória + sessionStorage). */
export function getCachedStaffOnlineStatuses(
  ids: string[]
): Record<string, StaffOnlineStatusRow> {
  hydrateCacheFromSession();
  const now = Date.now();
  const result: Record<string, StaffOnlineStatusRow> = {};
  for (const rawId of ids) {
    const id = String(rawId ?? '').trim().toLowerCase();
    if (!id) continue;
    const hit = cache.get(id);
    if (hit && now - hit.at <= CACHE_TTL_MS) {
      result[id] = hit.row;
    }
  }
  return result;
}

export type StaffOnlineProgress = (partial: Record<string, StaffOnlineStatusRow>) => void;

async function fetchOne(
  id: string,
  signal?: AbortSignal
): Promise<StaffOnlineStatusRow> {
  const res = await fetch(onlinecheckSingleUrl(id), {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) return toRow('unavailable', false);
  const data = (await res.json()) as { status?: string; online?: boolean };
  return toRow(String(data.status ?? 'offline'), Boolean(data.online));
}

/**
 * Busca em paralelo (pedido a pedido).
 * Evita o batch sequencial lento da API remota; em DEV cada pedido passa pelo proxy Vite.
 * `onProgress` permite pintar badges à medida que cada ID responde.
 */
export async function fetchStaffOnlineStatuses(
  ids: string[],
  signal?: AbortSignal,
  onProgress?: StaffOnlineProgress
): Promise<Record<string, StaffOnlineStatusRow>> {
  const unique = [
    ...new Set(
      ids
        .map((id) => String(id ?? '').trim().toLowerCase())
        .filter((id) => /^[a-z0-9_-]{1,64}$/.test(id))
    ),
  ];
  if (unique.length === 0) return {};

  hydrateCacheFromSession();

  const now = Date.now();
  const result: Record<string, StaffOnlineStatusRow> = {};
  const missing: string[] = [];

  for (const id of unique) {
    const hit = cache.get(id);
    if (hit && now - hit.at <= CACHE_TTL_MS) {
      result[id] = hit.row;
    } else {
      missing.push(id);
    }
  }

  if (missing.length === 0) {
    onProgress?.({ ...result });
    return result;
  }

  onProgress?.({ ...result });

  await Promise.all(
    missing.map(async (id) => {
      if (signal?.aborted) return;
      try {
        const row = await fetchOne(id, signal);
        remember(id, row);
        result[id] = row;
        onProgress?.({ ...result });
      } catch {
        if (signal?.aborted) return;
        const row = toRow('unavailable', false);
        remember(id, row);
        result[id] = row;
        onProgress?.({ ...result });
      }
    })
  );

  persistCacheToSession();

  for (const id of unique) {
    if (!result[id]) {
      result[id] = toRow('offline', false);
    }
  }

  return result;
}

/** Prefetch em background (ex.: quando o CMS hidrata a equipa). */
export function prefetchStaffOnlineStatuses(ids: string[]): void {
  const unique = [
    ...new Set(
      ids
        .map((id) => String(id ?? '').trim().toLowerCase())
        .filter((id) => /^[a-z0-9_-]{1,64}$/.test(id))
    ),
  ];
  if (unique.length === 0) return;
  const cached = getCachedStaffOnlineStatuses(unique);
  if (unique.every((id) => cached[id])) return;
  void fetchStaffOnlineStatuses(unique);
}
